import { useCallback, useMemo } from "react";

import { usePermissions } from "@/hooks/usePermissions";
import {
  useGetDashboardLayoutQuery,
  useResetDashboardLayoutMutation,
  useSaveDashboardLayoutMutation,
} from "@/services/(company)/api.dashboard-layout";
import { DASHBOARD_SCOPE } from "@/types/(company)/dashboardLayout.interface";
import {
  DEFAULT_QUICK_ACTION_KEYS,
  MAX_PINNED_QUICK_ACTIONS,
  QUICK_ACTION_CATALOG,
  QUICK_ACTION_STORED_SIZE,
  QUICK_ACTIONS_LAYOUT_VERSION,
} from "./quick-actions.data";
import type { IQuickAction } from "./types";

const SCOPE = DASHBOARD_SCOPE.WORKSPACE_QUICK_ACTIONS;

const BY_KEY = new Map(QUICK_ACTION_CATALOG.map((entry) => [entry.key, entry]));

/**
 * The Workspace quick-actions bar: what this user may do, what they pinned, and
 * the two writes that change it.
 *
 * Permission is applied on read, never baked into what is stored. A pinned
 * action the role has since lost simply stops showing, and comes back by itself
 * if the right does — which is also why `save` carries those hidden keys along
 * rather than silently forgetting them.
 */
export function useQuickActions() {
  const { can, isLoading: isPermissionsLoading } = usePermissions();
  const { data, isLoading: isLayoutLoading } = useGetDashboardLayoutQuery({
    scope: SCOPE,
  });
  const [saveLayout, { isLoading: isSaving }] = useSaveDashboardLayoutMutation();
  const [resetLayout, { isLoading: isResetting }] =
    useResetDashboardLayoutMutation();

  // `can` is a new closure every render, so it cannot be a dependency. Fold its
  // answers into a string instead — that only changes when the permission set
  // does.
  const permissionSignature = QUICK_ACTION_CATALOG.map((entry) =>
    can(entry.section, entry.action) ? "1" : "0",
  ).join("");

  const available = useMemo<IQuickAction[]>(
    () =>
      QUICK_ACTION_CATALOG.filter(
        (_, index) => permissionSignature[index] === "1",
      ),
    [permissionSignature],
  );

  const isCustomized = data?.data?.isCustomized ?? false;

  /** Stored keys in bar order, or null while on the default. */
  const savedKeys = useMemo<string[] | null>(() => {
    if (!isCustomized) return null;
    return (data?.data?.layout?.widgets ?? []).map((widget) => widget.type);
  }, [data, isCustomized]);

  const pinned = useMemo<IQuickAction[]>(() => {
    const allowed = new Set(available.map((entry) => entry.key));
    const resolve = (keys: readonly string[]) =>
      keys
        .filter((key) => allowed.has(key))
        .map((key) => BY_KEY.get(key))
        .filter((entry): entry is IQuickAction => Boolean(entry));

    if (savedKeys) return resolve(savedKeys).slice(0, MAX_PINNED_QUICK_ACTIONS);

    const defaults = resolve(DEFAULT_QUICK_ACTION_KEYS);
    return (defaults.length > 0 ? defaults : available).slice(
      0,
      MAX_PINNED_QUICK_ACTIONS,
    );
  }, [available, savedKeys]);

  const save = useCallback(
    (keys: string[]) => {
      const allowed = new Set(available.map((entry) => entry.key));
      // Keys pinned before the role lost them: kept, after the visible ones.
      const hidden = (savedKeys ?? []).filter(
        (key) => BY_KEY.has(key) && !allowed.has(key) && !keys.includes(key),
      );

      return saveLayout({
        scope: SCOPE,
        version: QUICK_ACTIONS_LAYOUT_VERSION,
        widgets: [...keys, ...hidden].map((key) => ({
          instanceId: `qa_${key}`,
          type: key,
          size: QUICK_ACTION_STORED_SIZE,
        })),
      }).unwrap();
    },
    [available, savedKeys, saveLayout],
  );

  const reset = useCallback(
    () => resetLayout({ scope: SCOPE }).unwrap(),
    [resetLayout],
  );

  return {
    /** Every action this user is allowed to take, in catalogue order. */
    available,
    /** The bar, in the user's order. */
    pinned,
    isCustomized,
    isLoading: isPermissionsLoading || isLayoutLoading,
    isSaving: isSaving || isResetting,
    save,
    reset,
  };
}
